// Các màn hình của app.
//
// ĐIỀU HƯỚNG ĐƯỢC MÀU VỚI ĐƯỜNG DẪN (xem router.ts). `view` không còn là
// nguồn sự thật nữa — nó được SUY RA từ `path`. Nhờ vậy nút back của trình
// duyệt, F5 và bookmark đều hoạt động, và không thể lệch giữa "màn đang hiện"
// với "URL đang mở".
//
// Hệ quả có chủ ý: mỗi lần chuyển màn là một mục lịch sử. Ở màn làm bài, nút
// back của điện thoại sẽ quay về màn trước thay vì đóng app — người dùng vẫn
// bấm "Thoát" trong app để kết thúc phiên.

import { useCallback, useEffect, useState } from 'react'
import { api } from './api'
import type { DayStat, Result, Session, Topic, User } from './types'
import { ApiError } from './types'
import { NavBar } from './components/ui'
import { Login, Register, Welcome } from './screens/auth'
import { ChooseCount, ChooseExamKind, ChooseTopic, ExamSetup, Home, useTopics } from './screens/setup'
import { Practice, ResultView } from './screens/practice'
import { ProgressView } from './screens/progress'
import { WrongView } from './screens/wrong'
import { PayView } from './screens/pay'
import { AccountView } from './screens/account'
import { P, sessionIDOf, useRoute, viewOf } from './router'
import type { View } from './router'



/** Các màn cần đăng nhập. Mở thẳng URL mà chưa đăng nhập sẽ bị đưa về login. */
const NEEDS_AUTH: View[] = [
  'home', 'topic', 'count', 'practice', 'exam', 'examSetup', 'result', 'progress', 'wrong', 'pay', 'account',
]

export default function App() {
  const { path, go } = useRoute()
  const view = viewOf(path)

  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const [topic, setTopic] = useState<Topic | null>(null)
  const [due, setDue] = useState(0)
  const [byDay, setByDay] = useState<DayStat[]>([])
  const [checking, setChecking] = useState(true)

  const { topics, loading, error } = useTopics()

  // Hỏi xem phiên cookie còn sống không.
  //
  // Cookie HttpOnly sống lâu hơn phiên React. Mở lại tab là vào thẳng app mà
  // không cần đăng nhập — nếu không hỏi, mọi lần mở app đều hiện màn Welcome
  // dù đã đăng nhập. Mà `/auth/me` trả 401 thì đó là chuyện bình thường.
  useEffect(() => {
    api
      .me()
      .then(setUser)
      .catch(() => undefined)
      .finally(() => setChecking(false))
  }, [])

  // Chặn màn cần đăng nhập khi chưa đăng nhập.
  //
  // `replace` chứ không `push`: người dùng mở /progress lúc chưa đăng nhập rồi
  // đăng nhập xong — nếu để /progress trong lịch sử, bấm back sẽ quay lại
  // /progress và lại bị đá, thành vòng lặp không ra.
  useEffect(() => {
    if (checking) return
    if (NEEDS_AUTH.includes(view) && !user) {
      go(P.login, true)
    }
  }, [checking, view, user, go])

  // Số câu cần ôn lại + số câu đã làm hôm nay — chỉ tải khi cần tới màn Home.
  //
  // Cả hai lấy từ MỘT lần gọi `/progress`: ô "Mục tiêu hôm nay" và thẻ "Cần ôn
  // lại" nằm cạnh nhau, gọi hai endpoint riêng thì một cái có số một cái bị 0
  // lúc mạng chập chờn — trông như app tự bịa dữ liệu.
  const loadDue = useCallback(() => {
    api
      .progress()
      .then((p) => {
        setDue(p.due_for_review)
        setByDay(p.by_day)
      })
      .catch(() => undefined) // không có quyền / mất mạng thì để 0
  }, [])

  useEffect(() => {
    if (view === 'home') loadDue()
  }, [view, loadDue])

  // PHỤC HỒI PHIÊN ĐANG LÀM SAU KHI F5.
  //
  // URL có chứa session id (`/practice/<id>`), nên F5 giữa khi làm bài không
  // mất bài: server vẫn còn phiên, chỉ cần hỏi lại. Trước khi có router thì
  // F5 là xong phiên — mất luôn cả những câu đã trả lời.
  const routeSessionID = sessionIDOf(path)
  useEffect(() => {
    if (view !== 'practice' || !routeSessionID) return
    let alive = true
    api
      .getSession(routeSessionID)
      .then((s) => {
        if (alive) setSession(s)
      })
      .catch((e) => {
        // Phiên không còn (bị xoá, hoặc không phải của user này) → về trang chủ
        // thay vì đứng ở màn trắng.
        if (alive && (e as ApiError).status === 404) go(P.home, true)
      })
    return () => {
      alive = false
    }
  }, [view, routeSessionID, go])

  // ---- điều hướng ----

  function goTab(t: 'home' | 'practice' | 'exam' | 'chart') {
    switch (t) {
      case 'home':
        go(P.home)
        break
      case 'practice':
        go(user ? P.topic : P.register)
        break
      case 'exam':
        // Thi thử là luồng RIÊNG, không phải ôn luyện có đồng hồ: khác ở chỗ
        // có bước chọn số câu + thời gian, và đồng hồ đếm ngược luôn bật.
        go(user ? P.exam : P.register)
        break
      case 'chart':
        go(P.progress)
        break
    }
  }

  async function startExam(count: number, sec: number) {
    try {
      const s = await api.createSession('timed', '', count, sec)
      setSession(s)
      setResult(null)
      go(`${P.practice}/${s.id}`)
    } catch (e) {
      const a = e as ApiError
      alert(a.code === 'offline' ? 'Không có mạng. Bạn cần mạng để tải câu hỏi về máy.' : a.detail)
    }
  }

  async function start(count: number) {
    try {
      // mode=practice: không giới hạn thời gian. Đồng hồ đếm ngược là chế độ
      // riêng (thi thử), chưa bật.
      const s = await api.createSession('practice', topic?.slug ?? '', count)
      setSession(s)
      setResult(null)
      go(`${P.practice}/${s.id}`)
    } catch (e) {
      const a = e as ApiError
      // Mất mạng khi bấm "Bắt đầu" thì không báo đỏ — nói rõ là mạng, để
      // người dùng biết chờ thay vì nghĩ app hỏng.
      alert(a.code === 'offline' ? 'Không có mạng. Bạn cần mạng để tải câu hỏi về máy.' : a.detail)
    }
  }

  // BÁO ĐƯỜNG DẪN CHO APP BỌC WEB.
  //
  // `go()` xuất hiện ở đây một lần duy nhất, nên đây là chỗ duy nhất cần canh —
  // mọi lần chuyển màn đều đi qua nó. WebView không thấy `pushState` của trang
  // (SPA đổi đường dẫn bằng History API, không phải điều hướng thật), nên app
  // bọc web không biết màn hình đã đổi; phải tự báo.
  //
  // Chỉ gửi khi đang chạy trong app: `window.ReactNativeWebView` chỉ tồn tại
  // trong WebView của react-native-webview. Ở trình duyệt thì bỏ qua — nếu
  // không thì mỗi lần chuyển màn lại ném lỗi.
  useEffect(() => {
    const bridge = (
      window as unknown as { ReactNativeWebView?: { postMessage: (s: string) => void } }
    ).ReactNativeWebView
    if (!bridge) return
    try {
      bridge.postMessage(JSON.stringify({ type: 'route', path }))
    } catch {
      // Cầu nối hỏng thì thôi: điều hướng trong app vẫn chạy, chỉ mất đồng
      // bộ với lớp native. Không nên làm hỏng cả app vì việc này.
    }
  }, [path])

  /** Luyện lại đúng những câu đã sai (màn Câu đã sai).
   *
   *  Gửi `question_ids` để backend lấy đúng các câu đó, thay vì lọc theo đề
   *  mục rồi hy vọng gặp lại — nếu không thì bấm một câu sai lại ra câu khác,
   *  tức nút bấm nói dối. */
  async function practiceIds(ids: string[]) {
    try {
      const s = await api.createSession('practice', '', ids.length, undefined, ids)
      setSession(s)
      setResult(null)
      go(`${P.practice}/${s.id}`)
    } catch (e) {
      const a = e as ApiError
      alert(a.code === 'offline' ? 'Không có mạng. Bạn cần mạng để tải câu hỏi về máy.' : a.detail)
    }
  }

  function onDone(r: Result) {
    setResult(r)
    go(P.result)
    loadDue()
  }

  /** Màn nào có tab bar ở đáy.
   *
   *  Mockup vẽ ở 3 màn: Trang chủ, Chọn chủ đề, Tiến độ. Đã thêm /exam theo yêu
   *  cầu — cùng lý do /topics: bấm tab "Thi thử" mà rơi vào màn trần thì chỉ còn
   *  nút back của trình duyệt, mà trên điện thoại nút đó là thoát app.
   *
   *  "Chọn số lượng" và "/exam/setup" thì KHÔNG có, dù là bước kế tiếp: hai màn
   *  chọn chủ đề / chọn loại đề là thao tác DUYỆT (đứng cạnh các tab khác), còn
   *  hai màn chọn số câu / chọn cấu trúc đề là đã bắt đầu một phiên làm bài.
   */
  const withTabs =
    !checking &&
    (view === 'home' || view === 'topic' || view === 'progress' || view === 'exam')

  /** Tab nào được sáng. `/topics` nằm trong nhánh "Ôn luyện", `/exam` trong
   *  nhánh "Thi thử" — cùng ý với việc gọi màn đó từ tab đó. */
  const activeTab: 'home' | 'practice' | 'exam' | 'chart' =
    view === 'progress' ? 'chart' : view === 'topic' ? 'practice' : view === 'exam' ? 'exam' : 'home'

  /** Đăng nhập/đăng ký xong thì lấy lại user rồi vào Home.
   *
   *  Không dùng lại dữ liệu trả về lúc đăng nhập: `register` và `login` đều
   *  trả profile, nhưng nếu sau này thêm trường vào một trong hai mà quên
   *  trường kia thì chỗ này im lặng thiếu. Gọi `/auth/me` là một request thừa
   *  mà luôn đúng.
   *
   *  `replace` — tránh để /login trong lịch sử, không thì bấm back sau khi
   *  đăng nhập sẽ quay về màn đăng nhập dù đã vào trong.
   */
  function afterAuth() {
    api.me().then((u) => {
      setUser(u)
      go(P.home, true)
    })
  }

  // Vào /result sau khi F5: kết quả chỉ nằm trong RAM nên không phục hồi được
  // (phiên đã nộp thì server vẫn giữ, nhưng lấy lại là việc khác). Dẫn về
  // trang chủ — để một màn trắng thì tệ hơn.
  useEffect(() => {
    if (!checking && view === 'result' && !result) go(P.home, true)
  }, [checking, view, result, go])

  return (
    <>
      {view === 'welcome' && (
        <Welcome
          loggedIn={!!user}
          onStart={() => go(user ? P.home : P.register)}
          onLogin={() => go(P.login)}
        />
      )}

      {view === 'login' && (
        <Login onDone={afterAuth} onGoRegister={() => go(P.register)} />
      )}

      {view === 'register' && (
        <Register onDone={afterAuth} onGoLogin={() => go(P.login)} />
      )}

      {view === 'home' && (
        <Home
          user={user?.email ?? ''}
          topics={topics}
          dueForReview={due}
          byDay={byDay}
          onPractice={() => go(P.topic)}
          onGoProgress={() => go(P.progress)}
          onGoWrong={() => go(P.wrong)}
          onGoAccount={() => go(P.account)}
          onGoPay={() => go(P.pay)}
        />
      )}

      {view === 'topic' && (
        <ChooseTopic
          topics={topics}
          loading={loading}
          error={error}
          onPick={(t) => {
            setTopic(t.slug === '' ? null : t)
            go(P.count)
          }}
          onBack={() => go(P.home)}
        />
      )}

      {view === 'count' && (
        <ChooseCount topic={topic} onStart={start} onBack={() => go(P.topic)} />
      )}

      {view === 'exam' && <ChooseExamKind onPick={() => go(P.examSetup)} onBack={() => go(P.home)} />}

      {view === 'examSetup' && (
        <ExamSetup onStart={startExam} onBack={() => go(P.exam)} />
      )}

      {view === 'practice' &&
        (session ? (
          <Practice
            session={session}
            onDone={onDone}
            onExit={() => {
              setSession(null)
              go(P.home)
            }}
          />
        ) : (
          // Đang hồi phiên từ URL — hiện trống có chủ đích, ngắn thôi. Hiện
          // "đang tải" quay vòng sẽ nháy màn khi mạng nhanh.
          <div />
        ))}

      {view === 'result' && result && (
        <ResultView
          r={result}
          onAgain={() => {
            setResult(null)
            go(P.topic)
          }}
          onHome={() => {
            setResult(null)
            go(P.home)
          }}
        />
      )}

      {view === 'progress' && (
        <ProgressView topics={topics} onGoWrong={() => go(P.wrong)} />
      )}

      {view === 'wrong' && (
        <WrongView onBack={() => go(P.progress)} onPractice={practiceIds} />
      )}

      {view === 'pay' && (
        <PayView
          hasFull={!!user?.full_library}
          onBack={() => go(P.home)}
          onDone={() => {
            // Sau khi được cấp quyền phải lấy lại `/auth/me` — `full_library`
            // trong RAM vẫn là giá trị CŨ, nên màn Trang chủ sẽ tiếp tục hiện
            // "chỉ 200 câu mẫu" sau khi đã mua.
            api.me().then(setUser).catch(() => undefined)
            go(P.home)
          }}
        />
      )}

      {view === 'account' && (
        <AccountView
          user={user}
          onBack={() => go(P.home)}
          onLoggedOut={() => {
            // Xoá user khỏi state, đổi route bằng `replace` để bấm back sau khi
            // đăng xuất không quay lại màn tài khoản đã bị khoá.
            setUser(null)
            setSession(null)
            setResult(null)
            go(P.welcome, true)
          }}
        />
      )}

      {withTabs && (
        <div className="sticky bottom-0">
          <NavBar active={activeTab} onGo={goTab} />
        </div>
      )}
    </>
  )
}






